import { apiInstance } from "@/utils/axios";
import { LOCALSTORAGE } from "@/utils/constants";
import {
  setupAxiosInterceptors,
  setupRefreshTimer,
} from "@/utils/tokenRefresh";
import * as Sentry from "@sentry/vue";
import { jwtDecode } from "jwt-decode";
import { defineStore } from "pinia";
import { computed, inject, ref } from "vue";

// Add function to decode JWT token
function parseJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = window.atob(base64);
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
}

export const useAuthStore = defineStore("authStore", () => {
  const accessToken = ref(
    localStorage.getItem(LOCALSTORAGE.ACCESS_TOKEN) || null
  );
  const userId = ref(localStorage.getItem(LOCALSTORAGE.USER_ID) || null);
  const companyId = ref(localStorage.getItem(LOCALSTORAGE.COMPANY_ID) || null);
  const projectId = ref(localStorage.getItem(LOCALSTORAGE.PROJECT_ID) || null);
  const user = ref(null);
  const company = ref(null);
  const isForgetPasswordLoading = ref(false);
  const isResetPasswordLoading = ref(false);
  const isRefreshingToken = ref(false);
  const refreshTokenTimeout = ref(null);
  const isInvitingAdmin = ref(false);
  const isAcceptingInvitation = ref(false);
  const isFetchingStatistics = ref(false);
  const isFetchingActivityLog = ref(false);
  const isTogglingUserStatus = ref(false);
  const isAssigningProjects = ref(false);
  const isAssigningUnits = ref(false);
  const userStatistics = ref(null);
  const userActivityLog = ref([]);

  const signup = ref({
    user: {
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
      company_name: "",
      company_email: "",
      company_phone: "",
      company_registration_number: "",
      company_address_line1: "",
      company_city: "",
      company_state: "",
      company_country: "",
      company_postal_code: "",
    },
    isLoading: false,
    response: null,
  });

  const signin = ref({
    user: {
      email: "",
      password: "",
      remember_me: false,
    },
    isLoading: false,
    response: null,
  });

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value);
  const getSignupResponse = computed(() => signup.value.response);
  const getSigninResponse = computed(() => signin.value.response);
  const isSignupLoading = computed(() => signup.value.isLoading);
  const isSigninLoading = computed(() => signin.value.isLoading);
  const getUserId = computed(() => userId.value);
  const getCompanyId = computed(() => companyId.value);
  const getProjectId = computed(() => projectId.value);
  const isRefreshingTokenStatus = computed(() => isRefreshingToken.value);

  // Add new computed properties
  const getAuthHeaders = computed(() => ({
    Authorization: `Bearer ${accessToken.value}`,
    "X-Company-ID": companyId.value,
    Accept: "application/json",
  }));

  const getCompanyIdFromToken = computed(() => {
    if (!accessToken.value) {
      return null;
    }

    const decodedToken = jwtDecode(accessToken.value);
    return decodedToken.company_id || null;
  });

  // Move posthog to store level
  const posthog = inject("posthog", null);

  // Callback to update store state when token is refreshed by interceptor
  const onTokenRefreshed = (data) => {
    const { access_token, user: userData, company: companyData } = data;

    // Update reactive state
    if (access_token) {
      accessToken.value = access_token;
    }

    if (userData) {
      user.value = userData;

      // Update user ID
      if (userData.user_id || userData.id) {
        const userIdToSet = userData.user_id || userData.id;
        userId.value = userIdToSet.toString();
      }

      // Update company ID from user data if available
      if (userData.company_id) {
        companyId.value = userData.company_id.toString();
      }
    }

    if (companyData) {
      company.value = companyData;

      // Update company ID from company data if available
      if (companyData.company_id) {
        companyId.value = companyData.company_id.toString();
      }
    }

    // Update refresh timer
    if (access_token && refreshTokenTimeout.value) {
      clearTimeout(refreshTokenTimeout.value);
      refreshTokenTimeout.value = setupRefreshTimer(
        access_token,
        refreshToken,
        true
      );
    }
  };

  // Initialize interceptors immediately instead of using onMounted
  setupAxiosInterceptors(true, false, onTokenRefreshed);

  // Actions
  async function signUp() {
    try {
      signup.value.isLoading = true;
      const response = await apiInstance.post(
        `api/admin/auth/register`,
        signup.value.user,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const {
        access_token,
        user: userData,
        company: companyData,
      } = response.data;

      if (access_token) {
        setAccessToken(access_token);
      }

      user.value = userData;
      company.value = companyData;
      signup.value.response = response.data;

      Sentry.setUser({
        id: userData.id,
        email: userData.email,
        company: companyData.name,
      });
    } catch (error) {
      signup.value.response = null;
      throw error;
    } finally {
      signup.value.isLoading = false;
    }
  }

  async function signIn() {
    signin.value.isLoading = true;

    try {
      const response = await apiInstance.post(
        `api/admin/auth/login`,
        {
          email: signin.value.user.email,
          password: signin.value.user.password,
          remember_me: signin.value.user.remember_me,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      const {
        access_token,
        user: userData,
        company: companyData,
      } = response.data;

      if (!access_token) {
        throw new Error("No access token received");
      }

      // Set access token first to ensure headers are set
      setAccessToken(access_token);

      // Set user and company data
      user.value = userData;
      company.value = companyData;

      // Store user role in localStorage
      if (userData?.role) {
        localStorage.setItem(LOCALSTORAGE.USER_ROLE, userData.role);
      }

      // Set company ID and add to headers
      if (companyData?.company_id) {
        companyId.value = companyData.company_id;
        localStorage.setItem(
          LOCALSTORAGE.COMPANY_ID,
          companyData.company_id.toString()
        );
      } else {
        throw new Error("No company ID received");
      }

      signin.value.response = response.data;

      // Only call posthog if it exists
      if (posthog) {
        posthog.identify(userData.id, {
          email: userData.email,
          name: userData.name,
          company: companyData.name,
        });
      }

      Sentry.setUser({
        id: userData.id,
        email: userData.email,
        company: companyData.name,
      });

      return response.data;
    } catch (error) {
      signin.value.response = null;
      clearAuthData(); // Clear any partial auth data on error
      throw error;
    } finally {
      signin.value.isLoading = false;
    }
  }

  async function refreshToken() {
    if (!accessToken.value) {
      throw new Error("No access token available for refresh");
    }

    try {
      isRefreshingToken.value = true;
      const response = await apiInstance.post(
        "api/admin/auth/refresh",
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken.value}`,
          },
        }
      );

      const {
        access_token,
        user: userData,
        company: companyData,
      } = response.data;
      if (!access_token) {
        throw new Error("No access token received during refresh");
      }

      setAccessToken(access_token);

      if (userData) {
        user.value = userData;
        if (userData.company_id) {
          companyId.value = userData.company_id;
          localStorage.setItem(
            LOCALSTORAGE.COMPANY_ID,
            userData.company_id.toString()
          );
        }
      }
      if (companyData) {
        company.value = companyData;
      }

      if (refreshTokenTimeout.value) {
        clearTimeout(refreshTokenTimeout.value);
      }
      refreshTokenTimeout.value = setupRefreshTimer(
        access_token,
        refreshToken,
        true
      );

      return response.data;
    } catch (error) {
      console.error("Token refresh failed:", error);

      if (error.response) {
        switch (error.response.status) {
          case 401:
            clearAuthData();
            break;
          case 403:
            console.error(
              "Access denied. Please use the appropriate portal for your role."
            );
            break;
          case 500:
            console.error("Server error occurred during token refresh");
            break;
          default:
            console.error(
              "Token refresh failed:",
              error.response.data?.message || error.message
            );
        }
      }

      throw error;
    } finally {
      isRefreshingToken.value = false;
    }
  }

  async function logout(skipApiCall = false) {
    try {
      if (!skipApiCall) {
        await apiInstance.post(
          "api/admin/auth/logout",
          {},
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${accessToken.value}`,
            },
          }
        );
      }
      clearAuthData();
    } catch (error) {
      clearAuthData();
      throw error;
    }
  }

  function clearAuthData() {
    if (refreshTokenTimeout.value) {
      clearTimeout(refreshTokenTimeout.value);
      refreshTokenTimeout.value = null;
    }

    // Clear all auth-related state
    accessToken.value = null;
    userId.value = null;
    companyId.value = null;
    projectId.value = null;
    user.value = null;
    company.value = null;

    // Clear all auth-related localStorage
    localStorage.removeItem(LOCALSTORAGE.ACCESS_TOKEN);
    localStorage.removeItem(LOCALSTORAGE.USER_ID);
    localStorage.removeItem(LOCALSTORAGE.COMPANY_ID);
    localStorage.removeItem(LOCALSTORAGE.PROJECT_ID);
    localStorage.removeItem(LOCALSTORAGE.USER_ROLE);

    // Clear API headers
    delete apiInstance.defaults.headers.common["Authorization"];
    delete apiInstance.defaults.headers.common["X-Company-ID"];

    // Reset form data
    signup.value.user = {
      name: "",
      email: "",
      password: "",
      password_confirmation: "",
      company_name: "",
      company_email: "",
      company_phone: "",
      company_registration_number: "",
      company_address_line1: "",
      company_city: "",
      company_state: "",
      company_country: "",
      company_postal_code: "",
    };
    signin.value.user = { email: "", password: "", remember_me: false };
  }

  function updateSignupUserData(payload) {
    if (payload instanceof FormData) {
      const file = payload.get("company_logo");
      if (file) {
        signup.value.user.company_logo = file;
      }
      return;
    }
    signup.value.user = { ...signup.value.user, ...payload };
  }

  function updateSigninUserData(payload) {
    signin.value.user = { ...signin.value.user, ...payload };
  }

  function setAccessToken(token) {
    if (!token) {
      return;
    }

    accessToken.value = token;
    localStorage.setItem(LOCALSTORAGE.ACCESS_TOKEN, token);
    apiInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const tokenData = parseJwt(token);
    if (tokenData) {
      const { user_id, company_ids, role, default_access } = tokenData;

      // Set user ID
      if (user_id) {
        userId.value = user_id;
        localStorage.setItem(LOCALSTORAGE.USER_ID, user_id.toString());
      }

      // Get company ID from default_access or company_ids
      let companyIdToUse = default_access?.company_id;
      if (!companyIdToUse && company_ids && company_ids.length > 0) {
        companyIdToUse = company_ids[0];
      }

      if (companyIdToUse) {
        companyId.value = companyIdToUse;
        localStorage.setItem(
          LOCALSTORAGE.COMPANY_ID,
          companyIdToUse.toString()
        );
        apiInstance.defaults.headers.common["X-Company-ID"] =
          companyIdToUse.toString();
      }

      // Set role
      if (role) {
        localStorage.setItem(LOCALSTORAGE.USER_ROLE, role);
      }

      // Setup refresh timer
      if (refreshTokenTimeout.value) {
        clearTimeout(refreshTokenTimeout.value);
      }
      refreshTokenTimeout.value = setupRefreshTimer(token, refreshToken, true);
    }
  }

  async function forgetPassword(email) {
    try {
      isForgetPasswordLoading.value = true;
      const response = await apiInstance.post(
        "api/admin/auth/forget-password", // Note single "t" to match Laravel route
        { email },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      // Error handling
    } finally {
      isForgetPasswordLoading.value = false;
    }
  }

  async function resetPassword(payload) {
    try {
      isResetPasswordLoading.value = true;
      const response = await apiInstance.post(
        "api/admin/auth/reset-password", // Now matches the backend route
        {
          token: payload.token,
          email: payload.email,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
        }
      );
      return response.data;
    } catch (error) {
      console.error("Reset error:", error.response?.data);
      throw error;
    } finally {
      isResetPasswordLoading.value = false;
    }
  }

  async function initializeAuth() {
    const token = localStorage.getItem(LOCALSTORAGE.ACCESS_TOKEN);

    if (token) {
      const decodedToken = parseJwt(token);

      if (decodedToken) {
        // Check if token is expired or about to expire
        const now = Date.now() / 1000;
        const tokenExp = decodedToken.exp;
        const timeUntilExp = tokenExp - now;

        // If token is expired or will expire in less than 5 minutes, refresh it
        if (timeUntilExp <= 0) {
          try {
            await refreshToken();
          } catch (error) {
            clearAuthData();
            return;
          }
        } else if (timeUntilExp < 300) {
          // 5 minutes
          try {
            await refreshToken();
          } catch (error) {
            // If refresh fails but token is still valid, continue with current token
            if (timeUntilExp > 0) {
              setAccessToken(token);
            } else {
              clearAuthData();
              return;
            }
          }
        } else {
          setAccessToken(token);
        }
      }
    }
  }

  async function inviteAdmin(payload) {
    try {
      isInvitingAdmin.value = true;
      const response = await apiInstance.post(
        "api/admin/auth/invite",
        {
          email: payload.email,
          company_id: payload.company_id,
          role: payload.role,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken.value}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      isInvitingAdmin.value = false;
    }
  }

  async function acceptInvitation(payload) {
    try {
      isAcceptingInvitation.value = true;
      const response = await apiInstance.post(
        "api/admin/auth/accept-invitation",
        {
          token: payload.token,
          email: payload.email,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      isAcceptingInvitation.value = false;
    }
  }

  async function fetchUserStatistics() {
    try {
      isFetchingStatistics.value = true;
      const response = await apiInstance.get("/api/admin/auth/statistics");
      userStatistics.value = response.data;
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      isFetchingStatistics.value = false;
    }
  }

  async function fetchUserActivityLog(userId) {
    try {
      isFetchingActivityLog.value = true;
      const response = await apiInstance.get(
        `/api/admin/auth/activity-log/${userId}`
      );
      userActivityLog.value = response.data;
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      isFetchingActivityLog.value = false;
    }
  }

  async function toggleUserStatus(userId) {
    try {
      isTogglingUserStatus.value = true;
      const response = await apiInstance.post(
        `/api/admin/auth/toggle-status/${userId}`
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      isTogglingUserStatus.value = false;
    }
  }

  async function assignProjectsToUser(userId, projectIds) {
    try {
      isAssigningProjects.value = true;
      const response = await apiInstance.post(
        `/api/admin/auth/assign-projects/${userId}`,
        {
          project_ids: projectIds,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      isAssigningProjects.value = false;
    }
  }

  async function assignUnitsToUser(userId, payload) {
    try {
      isAssigningUnits.value = true;
      const response = await apiInstance.post(
        `/api/admin/auth/assign-units/${userId}`,
        {
          unit_ids: payload.unit_ids,
          assignment_type: payload.assignment_type,
          rental_dates: payload.rental_dates,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      isAssigningUnits.value = false;
    }
  }

  return {
    // State
    accessToken,
    userId,
    companyId,
    projectId,
    signup,
    signin,
    user,
    company,
    isForgetPasswordLoading,
    isResetPasswordLoading,
    isRefreshingToken,
    isInvitingAdmin,
    isAcceptingInvitation,
    isFetchingStatistics,
    isFetchingActivityLog,
    isTogglingUserStatus,
    isAssigningProjects,
    isAssigningUnits,
    userStatistics,
    userActivityLog,

    // Getters
    isAuthenticated,
    getSignupResponse,
    getSigninResponse,
    isSignupLoading,
    isSigninLoading,
    getUserId,
    getCompanyId,
    getProjectId,
    isRefreshingTokenStatus,

    // Actions
    signUp,
    signIn,
    refreshToken,
    logout,
    clearAuthData,
    updateSignupUserData,
    updateSigninUserData,
    setAccessToken,
    forgetPassword,
    resetPassword,
    getAuthHeaders,
    getCompanyIdFromToken,
    initializeAuth,
    inviteAdmin,
    acceptInvitation,
    fetchUserStatistics,
    fetchUserActivityLog,
    toggleUserStatus,
    assignProjectsToUser,
    assignUnitsToUser,
  };
});
